/**
 * RV-H: Escalation queue tile + composer
 *
 * Surfaces the operator escalation queue for reviews (RV-F backend).
 * Two tiles: "My escalations" (escalated_to = current user) and
 * "Team escalations" (everything in the tenant). Admins see both
 * counters; non-admin operators see Team as a secondary stat only.
 *
 * Clicking a queue row opens the response composer prefilled from the
 * first RV-E template whose ratingScope matches the review's rating
 * bucket — falling back to a "any" scoped template. No mocks.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import {
  AlertCircle,
  ArrowUpRight,
  Inbox,
  Send,
  Star,
  Users,
  X,
} from "lucide-react";

interface QueueItem {
  reviewId: string;
  rating: number;
  text: string | null;
  authorName: string | null;
  platform: string;
  publishedAt: string | null;
  escalatedAt: string | null;
  escalatedTo: string | null;
  isMine: boolean;
  hasResponse: boolean;
}

interface QueueResponse {
  data: {
    items: QueueItem[];
    total: number;
    mineCount: number;
  };
}

type RatingScope = "any" | "positive" | "neutral" | "negative";

interface Template {
  id: string;
  name: string;
  body: string;
  ratingScope: RatingScope;
}

interface TemplatesResponse {
  data: Template[];
}

function ratingToScope(rating: number): RatingScope {
  if (rating >= 4) return "positive";
  if (rating === 3) return "neutral";
  return "negative";
}

function pickTemplate(templates: Template[], rating: number): Template | null {
  const bucket = ratingToScope(rating);
  const exact = templates.find((t) => t.ratingScope === bucket);
  if (exact) return exact;
  const anyScope = templates.find((t) => t.ratingScope === "any");
  return anyScope ?? null;
}

function StarBar({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= rating ? "fill-[var(--color-warning)] text-[var(--color-warning)]" : "text-[var(--color-border)]"}`}
        />
      ))}
    </div>
  );
}

interface ComposerProps {
  item: QueueItem;
  templates: Template[];
  onClose: () => void;
  onSent: () => void;
}

function ResponseComposer({ item, templates, onClose, onSent }: ComposerProps) {
  const matched = useMemo(() => pickTemplate(templates, item.rating), [templates, item.rating]);
  const [responseText, setResponseText] = useState(matched?.body ?? "");
  const [isSending, setIsSending] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);

  const handleSend = async () => {
    setIsSending(true);
    setComposerError(null);
    try {
      await apiFetch("/crm/reviews/respond", {
        method: "POST",
        body: JSON.stringify({ reviewId: item.reviewId, response: responseText }),
      });
      onSent();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Failed to send response.";
      setComposerError(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Respond to escalated review"
    >
      <div className="w-full max-w-2xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-[var(--color-foreground)]">
                {item.authorName ?? "Anonymous"}
              </span>
              <span className="text-sm text-[var(--color-muted-foreground)]">on {item.platform}</span>
            </div>
            <div className="mt-1">
              <StarBar rating={item.rating} />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
            aria-label="Close composer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {item.text && (
          <p className="mt-3 rounded-lg bg-[var(--color-surface)] p-3 text-sm italic text-[var(--color-muted-foreground)]">
            &ldquo;{item.text}&rdquo;
          </p>
        )}

        <div className="mt-4">
          <label
            htmlFor="rv-h-response"
            className="mb-1.5 block text-xs font-medium text-[var(--color-muted-foreground)]"
          >
            Response{" "}
            {matched ? (
              <span className="text-[var(--color-primary)]">
                — prefilled from template &ldquo;{matched.name}&rdquo;
              </span>
            ) : (
              <span>— no matching template, drafting from scratch</span>
            )}
          </label>
          <textarea
            id="rv-h-response"
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            rows={6}
            className="w-full resize-none rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </div>

        {composerError && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-3 py-2 text-sm text-[var(--color-error)]">
            <AlertCircle className="h-4 w-4" />
            {composerError}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending || responseText.trim() === ""}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Send className="h-3 w-3" />
            {isSending ? "Sending..." : "Send response"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EscalationQueuePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [scope, setScope] = useState<"mine" | "team">("mine");
  const [queue, setQueue] = useState<QueueResponse["data"] | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState<QueueItem | null>(null);

  const fetchQueue = useCallback(async () => {
    setError(null);
    try {
      const path =
        scope === "mine"
          ? "/crm/reviews/escalation-queue?mine=true"
          : "/crm/reviews/escalation-queue";
      const res = await apiFetch<QueueResponse>(path);
      setQueue(res.data);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Failed to load escalation queue.";
      setError(message);
      setQueue(null);
    } finally {
      setIsLoading(false);
    }
  }, [scope]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await apiFetch<TemplatesResponse>("/crm/reviews/response-templates");
      setTemplates(res.data);
    } catch {
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  const handleSent = async () => {
    setComposing(null);
    await fetchQueue();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Escalation Queue</h1>
        {isAdmin && (
          <span className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-primary)]">
            Admin view
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setScope("mine")}
          className={`rounded-xl border p-4 text-left transition-colors ${
            scope === "mine"
              ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
              : "border-[var(--color-border)] bg-[var(--color-background)] hover:bg-[var(--color-surface)]"
          }`}
          aria-pressed={scope === "mine"}
        >
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <Inbox className="h-4 w-4" /> My escalations
          </div>
          <div
            className="mt-2 text-2xl font-bold text-[var(--color-foreground)]"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {queue?.mineCount ?? 0}
          </div>
          <div className="text-xs text-[var(--color-muted-foreground)]">Assigned to you</div>
        </button>

        <button
          type="button"
          onClick={() => setScope("team")}
          className={`rounded-xl border p-4 text-left transition-colors ${
            scope === "team"
              ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
              : "border-[var(--color-border)] bg-[var(--color-background)] hover:bg-[var(--color-surface)]"
          }`}
          aria-pressed={scope === "team"}
        >
          <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            <Users className="h-4 w-4" /> Team escalations
          </div>
          <div
            className="mt-2 text-2xl font-bold text-[var(--color-foreground)]"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {scope === "team" ? (queue?.total ?? 0) : "—"}
          </div>
          <div className="text-xs text-[var(--color-muted-foreground)]">
            {isAdmin ? "All escalated reviews" : "Switch to view team queue"}
          </div>
        </button>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-[var(--color-surface)]" />
          ))}
        </div>
      )}

      {error && !isLoading && (
        <div className="flex items-center gap-2 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {!isLoading && !error && queue && queue.items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <Inbox className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]" />
          <p className="mt-3 font-medium text-[var(--color-foreground)]">No escalations in this queue</p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Reviews escalated to you (or your team) will appear here.
          </p>
        </div>
      )}

      {!isLoading && queue && queue.items.length > 0 && (
        <ul className="space-y-3">
          {queue.items.map((item) => (
            <li
              key={item.reviewId}
              className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[var(--color-foreground)]">
                      {item.authorName ?? "Anonymous"}
                    </span>
                    <span className="text-sm text-[var(--color-muted-foreground)]">on {item.platform}</span>
                    {item.isMine && (
                      <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-primary)]">
                        Mine
                      </span>
                    )}
                    {item.hasResponse && (
                      <span className="rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-success)]">
                        Responded
                      </span>
                    )}
                  </div>
                  <div className="mt-1">
                    <StarBar rating={item.rating} />
                  </div>
                  {item.text && (
                    <p className="mt-2 line-clamp-3 text-sm italic text-[var(--color-muted-foreground)]">
                      &ldquo;{item.text}&rdquo;
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setComposing(item)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                >
                  <ArrowUpRight className="h-3.5 w-3.5" />
                  Open & respond
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {composing && (
        <ResponseComposer
          item={composing}
          templates={templates}
          onClose={() => setComposing(null)}
          onSent={handleSent}
        />
      )}
    </div>
  );
}
